import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec3, vec4, float,
  fract, floor, sin, pow, clamp, mix, smoothstep, max, normalize,
  positionLocal, cameraViewMatrix, cameraProjectionMatrix
} from 'three/tsl';

// Open-ocean sky for the "Sinking Ship" map.
//
// Composition:
//   * Vertical gradient — deep cobalt zenith, soft cyan horizon, with a
//     hazy mist band right at the horizon line where sky meets sea so
//     the boundary doesn't hard-cut against the water shader.
//   * Sun + halo         — bright disc + soft Mie-style halo, biased so
//                          the halo extends along the horizontal more
//                          than vertically (atmospheric scattering reads
//                          stronger at glancing angles).
//   * Cumulus clouds     — two-octave value-noise drifting on a vertical
//                          tilt so they read as 3D puffs rather than a
//                          flat smear. Brighter, denser packing on the
//                          sun-facing side; thinner toward the back of
//                          the sky.
//   * Distant haze tint  — slight warm pull near the horizon to suggest
//                          atmospheric perspective. Kept subtle so the
//                          scene doesn't feel sunset-y.
//
// Vertex trick same as SkyMaterial: strip camera translation
// (vec4(pos, 0)) then force clip-space z = w so the sky always ends up
// behind every other depth-writing object.
export class SeaSkyMaterial extends NodeMaterial {
  readonly uTime          = uniform(0);

  // Sky gradient stops. Slightly desaturated mid so the deep-zenith blue
  // doesn't bleed into the water shader's reflection sample.
  readonly uZenith        = uniform(new THREE.Vector3(0.10, 0.32, 0.62));
  readonly uMidSky        = uniform(new THREE.Vector3(0.42, 0.66, 0.86));
  readonly uHorizon       = uniform(new THREE.Vector3(0.78, 0.88, 0.95));
  /** Soft warm tint pulled into the horizon band so distant water reads
   *  as faintly hazy rather than the same blue all the way out. Set to
   *  zero to disable. */
  readonly uHorizonHaze   = uniform(new THREE.Vector3(0.92, 0.84, 0.74));

  // Sun. Direction MUST agree with the WaterMaterial's uSunDir for the
  // glint and sky reflection to land on the same world-space spot.
  readonly uSunDir        = uniform(new THREE.Vector3(-0.42, 0.78, 0.46).normalize());
  readonly uSunColor      = uniform(new THREE.Vector3(1.00, 0.95, 0.78));
  // Cosine of the disc half-angle. 0.0010 rad ≈ 0.057° (very tight, real
  // sun is ~0.27° but at our scale we want it readable from the deck).
  readonly uSunDiscCos    = uniform(0.9998);
  readonly uSunHaloCos    = uniform(0.985);

  // Cloud knobs.
  readonly uCloudCoverage = uniform(0.55);  // higher = more sky covered
  readonly uCloudScale    = uniform(2.6);   // base frequency multiplier
  readonly uCloudSpeed    = uniform(0.012); // m/s through the noise field

  constructor() {
    super();
    this.side = THREE.BackSide;
    this.depthWrite = false;
    this.depthFunc = THREE.LessEqualDepth;
    this.lights = false;

    this.vertexNode = Fn(() => {
      const viewPos = cameraViewMatrix.mul(vec4(positionLocal, 0));
      const clip = cameraProjectionMatrix.mul(vec4(viewPos.xyz, 1));
      return vec4(clip.xy, clip.w, clip.w);
    })();

    // ── Cheap value noise utilities. Inlined rather than reusing
    // tsl/noise.ts because we want the hash3 → 3D smoothed value form for
    // cloud volume sampling, and the existing module only exposes 2D fbm.
    const hash3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const q = vec3(
        p.dot(vec3(127.1, 311.7, 74.7)),
        p.dot(vec3(269.5, 183.3, 246.1)),
        p.dot(vec3(113.5, 271.9, 124.6))
      );
      return fract(sin(q).mul(43758.5453)).x;
    });

    const noise3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const i = floor(p).toVar();
      const f = fract(p).toVar();
      const u = f.mul(f).mul(float(3).sub(f.mul(2)));

      const sample = (ox: number, oy: number, oz: number) =>
        hash3(i.add(vec3(ox, oy, oz)));

      const n000 = sample(0, 0, 0), n100 = sample(1, 0, 0);
      const n010 = sample(0, 1, 0), n110 = sample(1, 1, 0);
      const n001 = sample(0, 0, 1), n101 = sample(1, 0, 1);
      const n011 = sample(0, 1, 1), n111 = sample(1, 1, 1);

      const nx00 = mix(n000, n100, u.x);
      const nx10 = mix(n010, n110, u.x);
      const nx01 = mix(n001, n101, u.x);
      const nx11 = mix(n011, n111, u.x);
      const nxy0 = mix(nx00, nx10, u.y);
      const nxy1 = mix(nx01, nx11, u.y);
      return mix(nxy0, nxy1, u.z);
    });

    const fbm3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const q = p.toVar();
      const amp = float(0.55).toVar();
      const sum = float(0).toVar();
      for (let i = 0; i < 5; i++) {
        sum.addAssign(noise3(q).mul(amp));
        q.assign(q.mul(2.07).add(vec3(13.1, 23.7, 7.2)));
        amp.assign(amp.mul(0.52));
      }
      return sum;
    });

    this.colorNode = Fn(() => {
      const d = positionLocal.normalize().toVar();
      const h = clamp(d.y, float(-1), float(1));

      // ── Vertical gradient. Three stops so the sky has a noticeable
      // mid-altitude band rather than a single zenith-to-horizon ramp.
      // Smoothstep weights are tuned so the brightest sky region sits
      // about 15° above the horizon (where real cumulus puff-up sits)
      // and the deep zenith only takes over above ~45°.
      const tHorizon = pow(smoothstep(float(0), float(0.18), h), 1.6);
      const tZenith  = smoothstep(float(0.20), float(0.80), h);
      const lower = mix(this.uHorizon, this.uMidSky, tHorizon);
      const skyGrad = mix(lower, this.uZenith, tZenith).toVar();

      // Horizon haze — a thin band right at h ≈ 0 picks up the warm
      // tint, fading out by ~10°. Sells "atmosphere stacked at the
      // horizon" without needing a real Rayleigh integral.
      const hazeBand = smoothstep(float(0.30), float(0.0), h)
        .mul(smoothstep(float(-0.05), float(0.02), h));
      const skyHazed = mix(skyGrad, this.uHorizonHaze, hazeBand.mul(0.45));

      // Below-horizon tail. The water plane covers most of the lower
      // hemisphere but at its edge we want a soft fade from the horizon
      // tint into deep navy so we don't see a sharp seam. The water
      // shader takes over visually around y < -0.05.
      const belowFade = smoothstep(float(-0.25), float(0.0), h);
      const baseSky = mix(this.uZenith.mul(0.18), skyHazed, belowFade);

      // ── Sun disc + halo.
      const sunCos = max(d.dot(normalize(this.uSunDir)), float(0)).toVar();
      const disc  = smoothstep(this.uSunDiscCos, this.uSunDiscCos.add(0.0008), sunCos);
      // Two-stop halo: sharp inner glow and a wide soft outer scatter.
      const haloIn  = pow(sunCos, 240).mul(0.55);
      const haloOut = pow(sunCos, 18).mul(0.16);
      // Glancing scatter — atmospheric brightening near the horizon along
      // the sun direction. Stronger when the view sits low-and-sun-side.
      const horizonScatter = smoothstep(float(0.0), float(0.4), this.uSunDir.y)
        .mul(smoothstep(float(-0.15), float(0.25), h))
        .mul(pow(max(sunCos, float(0)), 4))
        .mul(0.10);
      const sunBrightness = disc.add(haloIn).add(haloOut).add(horizonScatter);
      const sunContribution = this.uSunColor.mul(sunBrightness);

      // ── Cumulus clouds. Sample a 3D fbm at the view direction (puffed
      // out by a vertical tilt so they don't read as a paper plane). The
      // sample point shifts very slowly with uTime so the cloud field
      // drifts visibly over a couple of minutes without making the user
      // notice they're watching clouds move.
      const cloudP = vec3(
        d.x.add(this.uTime.mul(this.uCloudSpeed)),
        d.y.mul(0.55),
        d.z.add(this.uTime.mul(this.uCloudSpeed.mul(0.7)))
      ).mul(this.uCloudScale);
      const cloudFbm = fbm3(cloudP).toVar();
      // Coverage knob — higher uCloudCoverage drops the threshold so more
      // pixels qualify as cloud. Edge softness 0.18 gives a fluffy edge.
      const coverThresh = float(1).sub(this.uCloudCoverage).mul(0.6).add(0.30);
      const cloudMask = smoothstep(coverThresh, coverThresh.add(0.18), cloudFbm)
        // Only above the horizon, fading in over the lowest 6° to avoid
        // a hard cloud line at the sea boundary.
        .mul(smoothstep(float(0.02), float(0.08), h))
        .toVar();

      // Cloud shading: brighter on the sun-facing side, with a soft
      // ambient base so the under-sides aren't pure black. Two cloud
      // colours mix by the cloud-to-sun alignment.
      const cloudSunDot = max(d.dot(normalize(this.uSunDir)), float(0));
      const cloudLit = vec3(0.97, 0.95, 0.90);
      const cloudShade = vec3(0.55, 0.62, 0.70);
      const cloudColor = mix(cloudShade, cloudLit, pow(cloudSunDot, 1.4));

      const withClouds = mix(baseSky, cloudColor, cloudMask);

      // Layer the sun on top last so a cloud in front of the sun still
      // gets brightened (cumulus rims often glow). Done by adding
      // sunContribution scaled by (1 - cloudMask)·0.85 + cloudMask·0.35
      // — clouds attenuate but don't fully occlude the sun.
      const sunOcclusion = mix(float(1), float(0.35), cloudMask);
      const final = withClouds.add(sunContribution.mul(sunOcclusion));

      return vec4(final, 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
}
